import { memo, startTransition, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Circle,
  CircleDot,
  Download,
  Minus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import PaginationFooter from "../Components/PaginationFooter";
import "./EquipmentReport.css";


// Export field toggles — keys must match the backend's _EXPORT_FIELDS.
const EXPORT_FIELD_OPTIONS = [
  { key: "status", label: "Status" },
  { key: "plan_start", label: "Planned Start" },
  { key: "plan_finish", label: "Planned Finish" },
  { key: "actual_start", label: "Actual Start" },
  { key: "actual_finish", label: "Actual Finish" },
];

const COLUMN_PRESETS = {
  minimal:   ["status"],
  schedule:  ["status", "plan_start", "plan_finish"],
  execution: ["status", "actual_start", "actual_finish"],
  all:       ["status", "plan_start", "plan_finish", "actual_start", "actual_finish"],
};

const getProjectIdValue = (projectValue) => {
  if (!projectValue) return null;
  if (typeof projectValue === "string") return projectValue;
  if (typeof projectValue === "object") return projectValue.id || projectValue.project_id || null;
  return null;
};

const isValidTimezone = (tz) => {
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

const SortableHeader = ({ label, sortKey, columnKey, sortDir, onSort, className, rowSpan }) => {
  const isActive = sortKey === columnKey;
  const ariaSort = !isActive ? "none" : sortDir === "asc" ? "ascending" : "descending";
  return (
    <th
      className={`${className} sortable-th${isActive ? " is-sorted" : ""}`}
      rowSpan={rowSpan}
      onClick={() => onSort?.(columnKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort?.(columnKey);
        }
      }}
      tabIndex={0}
      aria-sort={ariaSort}
      title={`Sort by ${label}`}
    >
      <span className="th-sort-label">
        {label}
        <span className="th-sort-icon" aria-hidden="true">
          {!isActive ? (
            <ArrowUpDown size={11} strokeWidth={2} />
          ) : sortDir === "asc" ? (
            <ArrowUp size={11} strokeWidth={2.5} />
          ) : (
            <ArrowDown size={11} strokeWidth={2.5} />
          )}
        </span>
      </span>
    </th>
  );
};

const EquipmentStatusTable = memo(function EquipmentStatusTable({
  processColumns,
  rows,
  processHeaderLabel,
  viewMode,
  renderScheduleDateDisplay,
  tooltipProps,
  onOpenEquipment,
  sortKey,
  sortDir,
  onSort,
}) {
  const isScheduleView = viewMode === "schedule";

  return (
    <table
      className={`equipment-report-table ${
        isScheduleView ? "equipment-report-table-schedule" : ""
      }`}
    >
      <thead>
        <tr>
          <th className="sticky-col sticky-tag" rowSpan={isScheduleView ? 2 : 1}>
            TAG
          </th>
          <SortableHeader
            label="%"
            columnKey="percent"
            className="sticky-col sticky-percent"
            rowSpan={isScheduleView ? 2 : 1}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          {processColumns.map((processName) =>
            isScheduleView ? (
              <th
                key={processName}
                className="process-group-head"
                title={processName}
                colSpan={2}
              >
                {processHeaderLabel(processName)}
              </th>
            ) : (
              <th
                key={processName}
                className="process-col"
                title={processName}
                rowSpan={1}
              >
                {processHeaderLabel(processName)}
              </th>
            ),
          )}
        </tr>
        {isScheduleView && (
          <tr>
            {processColumns.flatMap((processName) => [
                <th key={`${processName}-start`} className="process-col-sub schedule-subhead">
                  Start
                </th>,
                <th key={`${processName}-finish`} className="process-col-sub schedule-subhead">
                  Finish
                </th>
            ])}
          </tr>
        )}
      </thead>
      <tbody>
        {rows.map((equipment) => (
          <tr key={equipment.rowKey}>
            <td className="sticky-col sticky-tag equipment-tag-cell">
              <div className="equipment-tag-link-group">
                <span
                  className={
                    equipment.equipmentId
                      ? "equipment-tag-link-text"
                      : undefined
                  }
                  title={equipment.equipmentTag}
                >
                  {equipment.equipmentTag}
                </span>
                {equipment.equipmentId ? (
                  <button
                    type="button"
                    className="equipment-tag-open-btn"
                    onClick={() => onOpenEquipment?.(equipment)}
                    title={`Open equipment ${equipment.equipmentNumber || equipment.equipmentTag}`}
                    aria-label={`Open equipment ${equipment.equipmentNumber || equipment.equipmentTag}`}
                  >
                    <ArrowUpRight size={11} strokeWidth={2} />
                  </button>
                ) : null}
              </div>
            </td>
            <td className="sticky-col sticky-percent">
              <div
                className="progress-plan"
                title={
                  equipment.plannedPercent !== null
                    ? `Actual ${equipment.actualPercent}% vs Planned ${equipment.plannedPercent}% · ${equipment.completedCount}/${equipment.totalCount} steps`
                    : `Actual ${equipment.actualPercent}% · ${equipment.completedCount}/${equipment.totalCount} steps`
                }
              >
                <div className="progress-plan-top">
                  <span
                    className={`progress-plan-actual ${
                      equipment.actualPercent === 100 ? "progress-complete" : "progress-partial"
                    }`}
                  >
                    {equipment.actualPercent}%
                  </span>
                  {equipment.plannedPercent !== null ? (
                    <span className="progress-plan-planned">
                      / {equipment.plannedPercent}% plan
                    </span>
                  ) : null}
                  <span className="progress-plan-count">
                    {equipment.completedCount}/{equipment.totalCount}
                  </span>
                </div>
                <div className="progress-plan-bar">
                  <div
                    className={`progress-plan-fill progress-plan-fill-${equipment.progressTone}`}
                    style={{ width: `${equipment.actualPercent}%` }}
                  />
                  {equipment.plannedPercent !== null ? (
                    <span
                      className="progress-plan-marker"
                      style={{ left: `${equipment.plannedPercent}%` }}
                    />
                  ) : null}
                </div>
              </div>
            </td>
            {equipment.cells.flatMap((cell) =>
              isScheduleView ? (
                [
                  <td
                    key={`${cell.key}-start`}
                    className={`schedule-cell process-col-sub ${
                      cell.startCellType === "date" ? "schedule-cell-date" : ""
                    }`}
                  >
                    <Tooltip
                      arrow
                      placement="top"
                      title={cell.scheduleTooltip}
                      slotProps={tooltipProps}
                    >
                      {renderScheduleDateDisplay(cell.scheduleStartDisplay)}
                    </Tooltip>
                  </td>,
                  <td
                    key={`${cell.key}-finish`}
                    className={`schedule-cell process-col-sub ${
                      cell.finishCellType === "date" ? "schedule-cell-date" : ""
                    }`}
                  >
                    <Tooltip
                      arrow
                      placement="top"
                      title={cell.scheduleTooltip}
                      slotProps={tooltipProps}
                    >
                      {renderScheduleDateDisplay(cell.scheduleEndDisplay)}
                    </Tooltip>
                  </td>
                ]
              ) : (
                <td key={cell.key} className="status-cell process-col" title={`${cell.processName}: ${cell.statusLabel}`}>
                  {cell.statusType === "na" ? (
                    <span className="status-icon status-icon-na">
                      <Minus size={13} strokeWidth={2.2} />
                    </span>
                  ) : (
                    <Tooltip
                      arrow
                      placement="top"
                      title={cell.statusTooltip}
                      slotProps={tooltipProps}
                    >
                      <span className={`status-icon status-icon-${cell.statusType}`}>
                        {cell.statusType === "completed" ? (
                          <CheckCircle2 size={16} strokeWidth={2.2} />
                        ) : cell.statusType === "in-progress" ? (
                          <CircleDot size={16} strokeWidth={2.2} />
                        ) : (
                          <Circle size={16} strokeWidth={1.8} />
                        )}
                      </span>
                    </Tooltip>
                  )}
                </td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

// Shimmer placeholder shaped like the report matrix (sticky TAG + % + status
// columns) so the layout doesn't jump when real data swaps in. Mirrors the
// skeleton pattern used on the Equipments page.
const EquipmentReportSkeleton = ({ rows = 9, cols = 8 }) => (
  <div className="equipment-report-table-wrap">
    <table className="equipment-report-table">
      <thead>
        <tr>
          <th className="sticky-col sticky-tag">TAG</th>
          <th className="sticky-col sticky-percent">%</th>
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} className="process-col">
              <span className="equipment-report-skeleton-bar skeleton-head" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} className="equipment-report-skeleton-row">
            <td className="sticky-col sticky-tag equipment-tag-cell">
              <span className="equipment-report-skeleton-bar skeleton-tag" />
            </td>
            <td className="sticky-col sticky-percent">
              <span className="equipment-report-skeleton-bar skeleton-pct" />
            </td>
            {Array.from({ length: cols }).map((__, c) => (
              <td key={c} className="status-cell process-col">
                <span className="equipment-report-skeleton-dot" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function EquipmentReport({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState("");
  const [renderedGroupCode, setRenderedGroupCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [equipments, setEquipments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [viewMode, setViewMode] = useState("status");
  const [projectTimezone, setProjectTimezone] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportGroups, setExportGroups] = useState([]);
  const [exportFields, setExportFields] = useState(
    EXPORT_FIELD_OPTIONS.map((f) => f.key),
  );
  const [reportCacheByGroup, setReportCacheByGroup] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);
  // null = natural timeline order from the API. Otherwise "tag" | "percent".
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const tableScrollRef = useRef(null);
  const tooltipProps = useMemo(
    () => ({
      tooltip: { className: "equipment-report-tooltip-shell" },
      arrow: { className: "equipment-report-tooltip-arrow" },
    }),
    [],
  );

  const currentProjectLabel = useMemo(() => projectName || projectId || "-", [projectId, projectName]);
  const processColumns = useMemo(() => (Array.isArray(processes) ? processes : []), [processes]);
  const equipmentRows = useMemo(() => (Array.isArray(equipments) ? equipments : []), [equipments]);

  // Filter by TAG / equipment number on the client. We keep the full group's
  // rows for the matrix's column math (computed server-side over the whole
  // group) and only narrow the visible rows here.
  const filteredEquipmentRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return equipmentRows;
    return equipmentRows.filter((e) => {
      const tag =
        e?.equipment_number ||
        e?.equipment_tag_number ||
        e?.tag_number ||
        "";
      return String(tag).toLowerCase().includes(q);
    });
  }, [equipmentRows, debouncedSearch]);

  const totalFiltered = filteredEquipmentRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  // Guard against the page index dangling past the end after a filter narrows
  // the result set (e.g. typing while on page 5).
  const safePage = Math.min(currentPage, totalPages);

  // Sort the full filtered set BEFORE paginating so ordering is stable across
  // pages. Completion % is recomputed here (cheaply) rather than reusing the
  // tooltip-building normalization, which only runs on the visible page.
  const sortedEquipmentRows = useMemo(() => {
    if (!sortKey) return filteredEquipmentRows;

    const rawPercent = (equipment) => {
      const statusMap = equipment?.process_status || {};
      let done = 0, applicable = 0;
      processColumns.forEach((p) => {
        const s = String(statusMap?.[p]?.status || "").trim().toLowerCase();
        if (s === "n/a" || s === "na") return;
        applicable += 1;
        if (s === "completed") done += 1;
      });
      return applicable ? done / applicable : 0;
    };

    const dir = sortDir === "desc" ? -1 : 1;
    // Decorate with the original index so equal keys keep their timeline order.
    const decorated = filteredEquipmentRows.map((e, i) => ({ e, i }));
    decorated.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "percent") {
        cmp = rawPercent(a.e) - rawPercent(b.e);
      } else {
        const tag = (x) =>
          String(x?.equipment_number || x?.equipment_tag_number || x?.tag_number || "");
        cmp = tag(a.e).localeCompare(tag(b.e), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      if (cmp === 0) return a.i - b.i;
      return cmp * dir;
    });
    return decorated.map((x) => x.e);
  }, [filteredEquipmentRows, sortKey, sortDir, processColumns]);

  const pagedEquipmentRows = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return sortedEquipmentRows.slice(start, start + itemsPerPage);
  }, [sortedEquipmentRows, safePage, itemsPerPage]);

  const statusCounts = useMemo(() => {
    let completed = 0, inProgress = 0, pending = 0;
    filteredEquipmentRows.forEach((equipment) => {
      const statusMap = equipment?.process_status || {};
      let doneCount = 0, activeCount = 0, applicableCount = 0;
      processColumns.forEach((p) => {
        const s = String(statusMap?.[p]?.status || "").trim().toLowerCase();
        if (s === "n/a" || s === "na") return;
        applicableCount++;
        if (s === "completed") doneCount++;
        else if (s === "in progress" || s === "in_progress" || s === "started" || s === "ready_to_start") activeCount++;
      });
      if (applicableCount > 0 && doneCount === applicableCount) completed++;
      else if (doneCount > 0 || activeCount > 0) inProgress++;
      else pending++;
    });
    return { completed, inProgress, pending };
  }, [filteredEquipmentRows, processColumns]);

  const resetTableScroll = () => {
    requestAnimationFrame(() => {
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollLeft = 0;
        tableScrollRef.current.scrollTop = 0;
      }
    });
  };

  const handleGroupChange = (groupCode) => {
    if (!groupCode) return;
    setSelectedGroupCode(groupCode);
    startTransition(() => {
      setRenderedGroupCode(groupCode);
    });
    // Each group has its own timeline, so start it in natural order.
    setSortKey(null);
    setSortDir("asc");
    resetTableScroll();
  };

  // Cycle a column through: unsorted → ascending → descending → unsorted.
  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
    resetTableScroll();
  };

  // Debounce the search input so we don't re-filter/re-render on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Jump back to page 1 whenever the result set changes underneath the pager.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, renderedGroupCode, itemsPerPage]);

  const goToPage = (page) => {
    setCurrentPage(page);
    resetTableScroll();
  };

  const handleRefresh = () => {
    setReportCacheByGroup({});
    setRefreshKey((prev) => prev + 1);
    resetTableScroll();
  };


  const processHeaderLabel = (name = "") => {
    const normalized = String(name).trim();
    if (!normalized) return "-";

    const aliases = {
      "Start spading": "Start Spading",
      "Approval to spade": "Approval to Spade",
      "Spading list signed": "Spading Signed",
      "Eddy current": "Eddy Current",
      "PID Inspection": "PID Inspection",
      "IWR - Nozzle Repqlcement": "IWR",
      Torque: "Torque",
      "De-spading the equipment": "De-Spading",
      "Insulation Removal work": "Insulation Removal",
      "Scaffolding Erection Works": "Scaffolding",
      "Safe & ready for work execution & safety permit approval":
        "Safe Ready / Permit",
      "Dismental of Piping and Structure Material":
        "Piping / Structure Dismantle",
    };

    return aliases[normalized] || normalized;
  };

  const normalizeStatus = (status) => String(status || "").trim().toLowerCase();
  const normalizeStatusToken = (status) =>
    String(status || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");

  const getStatusType = (status) => {
    const s = normalizeStatus(status);
    if (s === "n/a" || s === "na") return "na";
    if (s === "completed") return "completed";
    if (
      s === "in progress" ||
      s === "in_progress" ||
      s === "started" ||
      s === "ready_to_start"
    ) {
      return "in-progress";
    }
    return "pending";
  };

  const getStatusColorClass = (status) => {
    const statusType = getStatusType(status);
    if (statusType === "completed") return "status-dot-completed";
    if (statusType === "in-progress") return "status-dot-progress";
    if (statusType === "pending") return "status-dot-pending";
    return "status-dot-na";
  };
  const isInProgressStatus = (status) => getStatusType(status) === "in-progress";

  const formatDateDdMmYyyy = (input) => {
    if (!input || String(input).toLowerCase() === "n/a") return null;
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return null;
    const opts = { day: "2-digit", month: "2-digit", year: "numeric" };
    if (projectTimezone) opts.timeZone = projectTimezone;
    // en-GB yields dd/mm/yyyy; switch separators to dashes.
    return date.toLocaleDateString("en-GB", opts).replace(/\//g, "-");
  };

  const formatTimeDisplay = (input) => {
    if (!input || String(input).toLowerCase() === "n/a") return null;
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return null;
    const opts = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    if (projectTimezone) opts.timeZone = projectTimezone;
    return date.toLocaleTimeString("en-GB", opts);
  };

  const renderScheduleDateDisplay = (value) => {
    if (value === "N/A") {
      return <span className="schedule-cell-inner">N/A</span>;
    }
    if (!value || value === "—") {
      return <span className="schedule-cell-inner">—</span>;
    }

    return (
      <span className="schedule-cell-inner schedule-cell-stacked">
        <span className="schedule-date">{value.date}</span>
        {value.time ? <span className="schedule-time">{value.time}</span> : null}
      </span>
    );
  };

  const getTooltipStatusLabel = (status) => {
    const token = normalizeStatusToken(status);
    if (token === "IN_PROGRESS") return "In Progress";
    if (token === "NOT_STARTED") return "Not Started";
    if (token === "READY_TO_START") return "Ready To Start";
    if (token === "N_A" || token === "NA") return "N/A";
    const normalized = String(status || "").trim();
    return normalized || "Pending";
  };

  const getTooltipStatusClass = (status) => {
    const statusType = getStatusType(status);
    if (statusType === "completed") return "report-tooltip-status completed";
    if (statusType === "in-progress") return "report-tooltip-status in-progress";
    if (statusType === "pending") return "report-tooltip-status pending";
    return "report-tooltip-status na";
  };

  const renderTooltipDateTime = (input) => {
    if (!input || String(input).toLowerCase() === "n/a") {
      return <span className="report-tooltip-empty">—</span>;
    }

    const date = new Date(input);
    if (Number.isNaN(date.getTime())) {
      return <span>{String(input)}</span>;
    }

    const dateOpts = { day: "2-digit", month: "2-digit", year: "numeric" };
    const timeOpts = { hour: "2-digit", minute: "2-digit", hour12: true };
    if (projectTimezone) {
      dateOpts.timeZone = projectTimezone;
      timeOpts.timeZone = projectTimezone;
    }

    const dateText = date.toLocaleDateString("en-GB", dateOpts).replace(/\//g, "-");
    const timeText = date.toLocaleTimeString("en-GB", timeOpts);

    return (
      <span className="report-tooltip-datetime">
        <span className="report-tooltip-date">{dateText}</span>
        <span className="report-tooltip-time">{timeText}</span>
      </span>
    );
  };

  const renderProcessTooltip = (
    processName,
    status,
    actualStartRaw,
    actualEndRaw,
    startEta,
    endEta,
  ) => (
    <div className="report-tooltip-card">
      <div className="report-tooltip-header">
        <div className="report-tooltip-process">{processName}</div>
        <span className={getTooltipStatusClass(status)}>
          <span className="report-tooltip-status-dot" />
          {getTooltipStatusLabel(status)}
        </span>
      </div>
      <div className="report-tooltip-grid">
        <div className="report-tooltip-row">
          <span className="report-tooltip-label">Planned start</span>
          <span className="report-tooltip-value">
            {renderTooltipDateTime(startEta)}
          </span>
        </div>
        <div className="report-tooltip-row">
          <span className="report-tooltip-label">Actual start</span>
          <span className="report-tooltip-value">
            {renderTooltipDateTime(actualStartRaw)}
          </span>
        </div>
        <div className="report-tooltip-row">
          <span className="report-tooltip-label">Planned finish</span>
          <span className="report-tooltip-value">
            {renderTooltipDateTime(endEta)}
          </span>
        </div>
        <div className="report-tooltip-row">
          <span className="report-tooltip-label">Actual finish</span>
          <span className="report-tooltip-value">
            {renderTooltipDateTime(actualEndRaw)}
          </span>
        </div>
      </div>
    </div>
  );


  const getProgressPercent = (equipment) => {
    const statusMap = equipment?.process_status || {};
    let completedCount = 0;
    let applicableCount = 0;
    const naProcesses = [];
    const completedProcesses = [];
    const incompleteProcesses = [];

    processColumns.forEach((processName) => {
      const status = statusMap?.[processName]?.status;
      const statusType = getStatusType(status);
      if (statusType === "na") {
        naProcesses.push(processName);
        return;
      }
      applicableCount += 1;
      if (statusType === "completed") {
        completedCount += 1;
        completedProcesses.push(processName);
      } else {
        incompleteProcesses.push({ name: processName, status });
      }
    });

    const tag = equipment?.equipment_number || equipment?.equipment_tag_number || equipment?.tag_number || "";
    if (tag === "223-34-04") {
      console.group(`[EquipmentReport debug] ${tag}`);
      console.log("Total group columns:", processColumns.length);
      console.log("Applicable (non-N/A):", applicableCount);
      console.log("Completed:", completedCount, completedProcesses);
      console.log("Incomplete:", incompleteProcesses);
      console.log("N/A (excluded from denominator):", naProcesses);
      console.log("Calculated %:", applicableCount ? Math.round((completedCount / applicableCount) * 100) : 0);
      console.groupEnd();
    }

    if (!applicableCount) return 0;
    return Math.round((completedCount / applicableCount) * 100);
  };

  const getProgressMetrics = (equipment) => {
    const statusMap = equipment?.process_status || {};
    let completedCount = 0;
    let applicableCount = 0;

    processColumns.forEach((processName) => {
      const status = statusMap?.[processName]?.status;
      const statusType = getStatusType(status);
      if (statusType === "na") return;
      applicableCount += 1;
      if (statusType === "completed") {
        completedCount += 1;
      }
    });

    const completedFromPayload = Number(equipment?.completed_process_count);
    const totalFromPayload = Number(equipment?.process_count);
    const completedCountSafe = Number.isFinite(completedFromPayload)
      ? completedFromPayload
      : completedCount;
    const totalCountSafe = Number.isFinite(totalFromPayload) && totalFromPayload > 0
      ? totalFromPayload
      : applicableCount;

    const actualFromPayload = Number(equipment?.actual_percent);
    const actualPercent = Number.isFinite(actualFromPayload)
      ? Math.max(0, Math.min(100, Math.round(actualFromPayload)))
      : totalCountSafe > 0
        ? Math.round((completedCountSafe / totalCountSafe) * 100)
        : 0;

    const plannedFromPayload = Number(equipment?.planned_percent);
    const plannedPercent = Number.isFinite(plannedFromPayload)
      ? Math.max(0, Math.min(100, Math.round(plannedFromPayload)))
      : null;

    const diff =
      plannedPercent === null ? 0 : actualPercent - plannedPercent;

    let progressTone = "normal";
    if (actualPercent >= 100) progressTone = "complete";
    else if (plannedPercent !== null && diff < -0.1) progressTone = "behind";
    else if (plannedPercent !== null && diff > 0.1) progressTone = "ahead";

    return {
      actualPercent,
      plannedPercent,
      completedCount: completedCountSafe,
      totalCount: totalCountSafe,
      progressTone,
    };
  };

  const normalizeEquipmentReport = useMemo(
    () =>
      pagedEquipmentRows.map((equipment) => {
        const percent = getProgressPercent(equipment);
        const progressMetrics = getProgressMetrics(equipment);
        const statusMap = equipment?.process_status || {};
        const equipmentTag =
          equipment?.equipment_number ||
          equipment?.equipment_tag_number ||
          equipment?.tag_number ||
          "-";
        return {
          rowKey: equipment.equipment_id || equipmentTag,
          equipmentId: equipment?.equipment_id || "",
          equipmentNumber: equipment?.equipment_number || equipmentTag,
          equipmentGroup: equipment?.equipment_group || renderedGroupCode || "",
          equipmentTag,
          percent,
          actualPercent: progressMetrics.actualPercent,
          plannedPercent: progressMetrics.plannedPercent,
          completedCount: progressMetrics.completedCount,
          totalCount: progressMetrics.totalCount,
          progressTone: progressMetrics.progressTone,
          cells: processColumns.map((processName) => {
            const row = statusMap?.[processName] || {};
            const status = row?.status;
            const statusType = getStatusType(status);
            const statusToken = normalizeStatusToken(status);
            const actualStartRaw = row?.actual_start;
            const actualEndRaw = row?.actual_end;
            const startEta = row?.start_eta;
            const endEta = row?.end_eta;
            const actualStart = formatDateDdMmYyyy(actualStartRaw);
            const actualEnd = formatDateDdMmYyyy(actualEndRaw);
            let scheduleStartDisplay = "—";
            let scheduleEndDisplay = "—";
            let startCellType = "dash";
            let finishCellType = "dash";

            if (statusType === "na" || statusToken === "N/A" || statusToken === "NA") {
              scheduleStartDisplay = "N/A";
              scheduleEndDisplay = "N/A";
              startCellType = "na";
              finishCellType = "na";
            } else if (statusToken === "NOT_STARTED") {
              scheduleStartDisplay = "—";
              scheduleEndDisplay = "—";
            } else if (statusToken === "IN_PROGRESS" || statusToken === "STARTED") {
              scheduleStartDisplay = actualStart
                ? { date: actualStart, time: formatTimeDisplay(actualStartRaw) }
                : "—";
              scheduleEndDisplay = "—";
              if (actualStart) startCellType = "date";
            } else if (statusToken === "COMPLETED") {
              scheduleStartDisplay = actualStart
                ? { date: actualStart, time: formatTimeDisplay(actualStartRaw) }
                : "—";
              scheduleEndDisplay = actualEnd
                ? { date: actualEnd, time: formatTimeDisplay(actualEndRaw) }
                : "—";
              if (actualStart) startCellType = "date";
              if (actualEnd) finishCellType = "date";
            }
            return {
              key: `${equipment.equipment_id}-${processName}`,
              processName,
              statusType,
              statusLabel: getTooltipStatusLabel(status),
              dotClass: getStatusColorClass(status),
              isInProgress: isInProgressStatus(status),
              statusTooltip: (
                renderProcessTooltip(
                  processName,
                  status,
                  actualStartRaw,
                  actualEndRaw,
                  startEta,
                  endEta,
                )
              ),
              scheduleTooltip: (
                renderProcessTooltip(
                  processName,
                  status,
                  actualStartRaw,
                  actualEndRaw,
                  startEta,
                  endEta,
                )
              ),
              scheduleStartDisplay,
              scheduleEndDisplay,
              startCellType,
              finishCellType,
            };
          }),
        };
      }),
    [pagedEquipmentRows, processColumns, renderedGroupCode, projectTimezone],
  );

  const openExportDialog = () => {
    setExportGroups(renderedGroupCode ? [renderedGroupCode] : []);
    setExportFields(EXPORT_FIELD_OPTIONS.map((f) => f.key));
    setExportOpen(true);
  };

  const toggleExportGroup = (code) => {
    setExportGroups((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleExportField = (key) => {
    setExportFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleExportXlsx = async () => {
    if (!projectId || exporting) return;
    // Preserve canonical field order regardless of toggle order.
    const orderedFields = EXPORT_FIELD_OPTIONS.filter((f) =>
      exportFields.includes(f.key),
    ).map((f) => f.key);

    if (exportGroups.length === 0 || orderedFields.length === 0) return;

    setExporting(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL}/equipment/report/${projectId}/export`,
        {
          params: {
            equipment_group: exportGroups.join(","),
            fields: orderedFields.join(","),
            format: "xlsx",
          },
          responseType: "blob",
        },
      );

      if (!res.data || (res.data.size !== undefined && res.data.size === 0)) {
        setReportError("No data to export for the selected groups.");
        setSnackbarOpen(true);
        return;
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const namePart =
        exportGroups.length === 1
          ? String(exportGroups[0]).replace(/[^A-Za-z0-9._-]+/g, "_") || "group"
          : `${exportGroups.length}_groups`;
      a.href = url;
      a.download = `equipment_report_${namePart}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      console.error("Failed to export equipment report:", err);
      setReportError("Failed to export equipment report.");
      setSnackbarOpen(true);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenEquipment = (equipment) => {
    const equipmentId = String(equipment?.equipmentId || "").trim();
    if (!equipmentId) return;

    const params = new URLSearchParams();
    params.set("highlightEquipmentId", equipmentId);

    const equipmentNumber = String(
      equipment?.equipmentNumber || equipment?.equipmentTag || "",
    ).trim();
    if (equipmentNumber) {
      params.set("equipmentNumber", equipmentNumber);
    }

    const groupCode = String(
      equipment?.equipmentGroup || renderedGroupCode || "",
    ).trim();
    if (groupCode) {
      params.set("equipmentGroup", groupCode);
    }

    navigate(`/equipments?${params.toString()}`);
  };


  useEffect(() => {
    const syncProjectFromStorage = () => {
      const storedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");
      const nextProjectId = getProjectIdValue(storedProject);
      setProjectId(nextProjectId);
    };

    syncProjectFromStorage();
    window.addEventListener("projectChanged", syncProjectFromStorage);
    return () => window.removeEventListener("projectChanged", syncProjectFromStorage);
  }, []);

  useEffect(() => {
    const fetchProjectName = async () => {
      if (!projectId) {
        setProjectName("");
        return;
      }

      try {
        const res = await axiosInstance.get("/api/get_project");
        const projects = res?.data?.data || [];
        const selected = projects.find((p) => p.project_id === projectId);
        setProjectName(selected?.name || "");
      } catch {
        setProjectName("");
      }
    };

    fetchProjectName();
  }, [projectId]);

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      if (!projectId) {
        setGroups([]);
        setSelectedGroupCode("");
        setRenderedGroupCode("");
        setError("Please select a project to view equipment groups.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await axiosInstance.get(`${API_URL}/equipment-groups/${projectId}`);
        const rows = Array.isArray(res.data) ? res.data : [];
        const activeGroupExists = rows.some(
          (row) => row?.equipment_group_code === selectedGroupCode,
        );
        const nextGroupCode = activeGroupExists
          ? selectedGroupCode
          : rows[0]?.equipment_group_code || "";
        setGroups(rows);
        setSelectedGroupCode(nextGroupCode);
        setRenderedGroupCode(nextGroupCode);
      } catch (err) {
        console.error("Failed to load equipment groups:", err);
        setGroups([]);
        setSelectedGroupCode("");
        setRenderedGroupCode("");
        setError("Failed to load equipment groups.");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchEquipmentGroups();
  }, [projectId, refreshKey]);

  useEffect(() => {
    const fetchEquipmentReport = async () => {
      if (!projectId || !renderedGroupCode) {
        setEquipments([]);
        setProcesses([]);
        setReportError("");
        return;
      }

      const cached = reportCacheByGroup[renderedGroupCode];
      if (cached) {
        setEquipments(Array.isArray(cached.equipments) ? cached.equipments : []);
        setProcesses(Array.isArray(cached.processes) ? cached.processes : []);
        if (isValidTimezone(cached.timezone)) setProjectTimezone(cached.timezone);
        setReportError("");
        return;
      }

      setReportLoading(true);
      setReportError("");
      try {
        const res = await axiosInstance.get(
          `${API_URL}/equipment/report/${projectId}?equipment_group=${encodeURIComponent(renderedGroupCode)}`,
        );
        const payload = res?.data || {};
        const nextEquipments = Array.isArray(payload.equipments) ? payload.equipments : [];
        const nextProcesses = Array.isArray(payload.processes) ? payload.processes : [];
        const nextTimezone = payload.timezone || "";
        setEquipments(nextEquipments);
        setProcesses(nextProcesses);
        if (isValidTimezone(nextTimezone)) setProjectTimezone(nextTimezone);
        setReportCacheByGroup((prev) => ({
          ...prev,
          [renderedGroupCode]: {
            equipments: nextEquipments,
            processes: nextProcesses,
            timezone: nextTimezone,
          },
        }));
      } catch (err) {
        console.error("Failed to load equipment report data:", err);
        setEquipments([]);
        setProcesses([]);
        setReportError("Failed to load equipment report data.");
        setSnackbarOpen(true);
      } finally {
        setReportLoading(false);
      }
    };

    fetchEquipmentReport();
  }, [projectId, renderedGroupCode, refreshKey]);

  useEffect(() => {
    // Reset cache when project changes so stale groups don't leak across projects.
    setReportCacheByGroup({});
    setSelectedGroupCode("");
    setRenderedGroupCode("");
  }, [projectId]);

  return (
    <div
      className={`page-wrapper text-center ${isSidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"}`}
      style={{
        backgroundColor: "#f4f5f7",
        height: "calc(100vh - 80px)",
        minHeight: 0,
        padding: "16px 20px 16px",
        marginLeft: isSidebarExpanded ? "220px" : "60px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box className="equipment-report-shell">
        <Box className="equipment-report-header">
          <Box className="equipment-report-header-main">
            <h2 className="page-title mb-0">Equipment Report</h2>
          </Box>
          <Box className="equipment-report-header-controls">
            <div className="equipment-report-search equipment-report-search-header">
              <Search size={15} strokeWidth={2} className="equipment-report-search-icon" />
              <input
                type="text"
                className="equipment-report-search-input"
                placeholder="Search Tag / Equipment No."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search equipment by tag"
              />
              {searchQuery ? (
                <button
                  type="button"
                  className="equipment-report-search-clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  title="Clear search"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              ) : null}
            </div>
            <Box className="equipment-report-view-toggle">
              <div className="report-view-segment" role="tablist" aria-label="Equipment report view">
                <button
                  type="button"
                  className={`report-view-btn ${viewMode === "status" ? "active" : ""}`}
                  onClick={() => setViewMode("status")}
                  aria-label="Status view"
                  aria-pressed={viewMode === "status"}
                >
                  <GridViewOutlined sx={{ fontSize: 16 }} />
                  Status
                </button>
                <button
                  type="button"
                  className={`report-view-btn ${viewMode === "schedule" ? "active" : ""}`}
                  onClick={() => setViewMode("schedule")}
                  aria-label="Schedule view"
                  aria-pressed={viewMode === "schedule"}
                >
                  <CalendarTodayOutlined sx={{ fontSize: 14 }} />
                  Schedule
                </button>
              </div>
              <Tooltip title="Refresh" arrow placement="top">
                <span className="report-icon-btn-wrap">
                  <button
                    type="button"
                    className="report-icon-btn"
                    onClick={handleRefresh}
                    disabled={loading || reportLoading || !projectId}
                    aria-label="Refresh equipment report"
                  >
                    <RefreshCw
                      size={15}
                      strokeWidth={2}
                      className={reportLoading ? "refresh-icon-spinning" : ""}
                    />
                  </button>
                </span>
              </Tooltip>
              <button
                type="button"
                className="report-export-btn"
                onClick={openExportDialog}
                disabled={
                  exporting ||
                  reportLoading ||
                  !renderedGroupCode ||
                  equipmentRows.length === 0
                }
                aria-label="Export to Excel"
                title="Export to Excel (.xlsx)"
              >
                <Download size={14} strokeWidth={2} />
                Export
              </button>
            </Box>
          </Box>
        </Box>

        <Box className="equipment-report-tabs-wrap">
          {loading && (
            <Typography className="equipment-report-message">
              Loading equipment groups...
            </Typography>
          )}

          {!loading && error && (
            <Typography className="equipment-report-message">
              {error}
            </Typography>
          )}

          {!loading && !error && groups.length === 0 && (
            <Typography className="equipment-report-message">
              No equipment groups found for this project.
            </Typography>
          )}

          {!loading && !error && groups.length > 0 && (
            <div className="equipment-report-tab-buttons" role="tablist" aria-label="Equipment groups">
              {groups.map((group) => {
                const groupCode = group.equipment_group_code;
                const isActive = selectedGroupCode === groupCode;
                return (
                  <button
                    key={groupCode}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleGroupChange(groupCode)}
                    className={`equipment-report-tab-btn ${isActive ? "active" : ""}`}
                  >
                    {group.equipment_group_name || groupCode}
                  </button>
                );
              })}
            </div>
          )}
        </Box>

        <Box className="equipment-report-matrix-card">
          <Box className="equipment-report-toolbar">
            <div className="equipment-report-toolbar-right">
              {viewMode === "status" && (
                <div className="equipment-report-status-summary" aria-label="Equipment report status summary">
                  <span className="equipment-report-status-item">
                    Completed
                    <span className="equipment-report-status-count">{statusCounts.completed}</span>
                  </span>
                  <span className="equipment-report-status-item">
                    In Progress
                    <span className="equipment-report-status-count">{statusCounts.inProgress}</span>
                  </span>
                  <span className="equipment-report-status-item">
                    Pending
                    <span className="equipment-report-status-count">{statusCounts.pending}</span>
                  </span>
                </div>
              )}
            </div>
          </Box>

          {reportLoading && (
            <EquipmentReportSkeleton cols={Math.max(processColumns.length, 6)} />
          )}

          {!reportLoading && reportError && (
            <Typography className="equipment-report-message">
              {reportError}
            </Typography>
          )}

          {!reportLoading && !reportError && groups.length > 0 && equipmentRows.length === 0 && (
            <Typography className="equipment-report-message">
              No equipment report data found for this equipment group.
            </Typography>
          )}

          {!reportLoading && !reportError && equipmentRows.length > 0 && totalFiltered === 0 && (
            <Typography className="equipment-report-message">
              No equipment matches “{debouncedSearch.trim()}”.
            </Typography>
          )}

          {!reportLoading && !reportError && totalFiltered > 0 && (
            <>
              <div ref={tableScrollRef} className="equipment-report-table-wrap">
                <EquipmentStatusTable
                  processColumns={processColumns}
                  rows={normalizeEquipmentReport}
                  processHeaderLabel={processHeaderLabel}
                  viewMode={viewMode}
                  renderScheduleDateDisplay={renderScheduleDateDisplay}
                  tooltipProps={tooltipProps}
                  onOpenEquipment={handleOpenEquipment}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </div>
              <div className="equipment-report-pagination-footer">
                <PaginationFooter
                  totalRecords={totalFiltered}
                  currentPage={safePage}
                  itemsPerPage={itemsPerPage}
                  setCurrentPage={goToPage}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={exportOpen}
        onClose={() => !exporting && setExportOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: "equipment-export-dialog" }}
      >
        <DialogTitle className="equipment-export-dialog-title">
          <div className="equipment-export-title-row">
            <span className="equipment-export-eyebrow">Equipment Report</span>
            <span className="equipment-export-title-sep">·</span>
            <span className="equipment-export-title-text">Export to Excel</span>
          </div>
          <div className="equipment-export-summary">
            {exportGroups.length > 0 || exportFields.length > 0 ? (
              <>
                <span>{exportGroups.length} {exportGroups.length === 1 ? "group" : "groups"}</span>
                <span className="export-summary-dot">·</span>
                <span>{exportFields.length} {exportFields.length === 1 ? "column" : "columns"} selected</span>
              </>
            ) : (
              <span>No groups or columns selected</span>
            )}
          </div>
        </DialogTitle>

        <DialogContent className="equipment-export-dialog-content">
          {/* Step 1 — Equipment Groups */}
          <div className="equipment-export-section">
            <div className="equipment-export-section-head">
              <div className="equipment-export-step-label">
                <span className="export-step-num">Step 1</span>
                <span className="export-step-title">Select Equipment Groups</span>
              </div>
              <div className="equipment-export-head-right">
                <button
                  type="button"
                  className="equipment-export-select-all"
                  onClick={() =>
                    exportGroups.length === groups.length
                      ? setExportGroups([])
                      : setExportGroups(groups.map((g) => g.equipment_group_code))
                  }
                >
                  {exportGroups.length === groups.length ? "Deselect all" : "Select all"}
                </button>
                <span className="equipment-export-count">{exportGroups.length} selected</span>
              </div>
            </div>
            <div className="equipment-export-options-grid">
              {groups.map((group) => {
                const code = group.equipment_group_code;
                const label = group.equipment_group_name || code;
                const isSelected = exportGroups.includes(code);
                const eqCount = group.equipment_count ?? group.count ?? null;
                const metaParts = [];
                if (code && code !== label) metaParts.push(`Code: ${code}`);
                if (eqCount != null) metaParts.push(`${eqCount} equipment`);
                return (
                  <button
                    key={code}
                    type="button"
                    className={`equipment-export-option${isSelected ? " is-selected" : ""}`}
                    onClick={() => toggleExportGroup(code)}
                    aria-pressed={isSelected}
                  >
                    <span className="equipment-export-option-check" aria-hidden="true">
                      {isSelected ? <Check size={12} strokeWidth={3} /> : null}
                    </span>
                    <span className="equipment-export-option-copy">
                      <span className="equipment-export-option-title">{label}</span>
                      {metaParts.length > 0 && (
                        <span className="equipment-export-option-meta">{metaParts.join(" · ")}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2 — Columns */}
          <div className="equipment-export-section">
            <div className="equipment-export-section-head">
              <div className="equipment-export-step-label">
                <span className="export-step-num">Step 2</span>
                <span className="export-step-title">Select Columns</span>
              </div>
              <div className="equipment-export-head-right">
                <button
                  type="button"
                  className="equipment-export-select-all"
                  onClick={() =>
                    exportFields.length === EXPORT_FIELD_OPTIONS.length
                      ? setExportFields([])
                      : setExportFields(EXPORT_FIELD_OPTIONS.map((f) => f.key))
                  }
                >
                  {exportFields.length === EXPORT_FIELD_OPTIONS.length ? "Deselect all" : "Select all"}
                </button>
                <span className="equipment-export-count">{exportFields.length} selected</span>
              </div>
            </div>
            <div className="export-presets-row">
              {Object.entries(COLUMN_PRESETS).map(([presetKey, presetFields]) => {
                const isActive = [...presetFields].sort().join(",") === [...exportFields].sort().join(",");
                return (
                  <button
                    key={presetKey}
                    type="button"
                    className={`export-preset-btn${isActive ? " is-active" : ""}`}
                    onClick={() => setExportFields(presetFields)}
                  >
                    {presetKey.charAt(0).toUpperCase() + presetKey.slice(1)}
                  </button>
                );
              })}
            </div>
            <div className="equipment-export-field-grid">
              {EXPORT_FIELD_OPTIONS.map((field) => {
                const isSelected = exportFields.includes(field.key);
                return (
                  <button
                    key={field.key}
                    type="button"
                    className={`equipment-export-option equipment-export-option-compact${isSelected ? " is-selected" : ""}`}
                    onClick={() => toggleExportField(field.key)}
                    aria-pressed={isSelected}
                  >
                    <span className="equipment-export-option-check" aria-hidden="true">
                      {isSelected ? <Check size={12} strokeWidth={3} /> : null}
                    </span>
                    <span className="equipment-export-option-title">{field.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>

        <DialogActions className="equipment-export-dialog-actions">
          <div className="export-footer-right">
            <Button
              className="equipment-export-cancel-btn"
              onClick={() => setExportOpen(false)}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button
              className="equipment-export-confirm-btn"
              onClick={handleExportXlsx}
              disabled={exporting || exportGroups.length === 0 || exportFields.length === 0}
              startIcon={
                exporting ? (
                  <CircularProgress size={14} thickness={5} color="inherit" />
                ) : (
                  <Download size={14} strokeWidth={2} />
                )
              }
            >
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="error">
          {error || reportError || "Something went wrong."}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default EquipmentReport;



